import toast from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  cancelTaxRateEditor,
  commitTaxRateForm,
  updateTaxRateForm,
} from '@store/slices/settingsTemplatesSlice';
import { useModal } from '@/hooks/useModal';
import { StandardModal } from '@/modals/StandardModal';

const TaxRateEditorModal = () => {
  const dispatch = useAppDispatch();
  const modal = useModal('settings.taxRates.editor');
  const { editingRateId, form } = useAppSelector(state => state.settingsTemplates.taxRateEditor);
  const isEditing = Boolean(editingRateId);

  const closeEditor = () => {
    dispatch(cancelTaxRateEditor());
    modal.close();
  };

  const handleSave = () => {
    if (!form.name.trim()) {
      toast.error('Tax rate name is required');
      return;
    }

    const rateNum = parseFloat(form.rate);
    if (Number.isNaN(rateNum) || rateNum < 0) {
      toast.error('Enter a valid tax rate');
      return;
    }

    dispatch(commitTaxRateForm());
    toast.success(isEditing ? 'Tax rate updated' : 'Tax rate added');
    modal.close();
  };

  return (
    <StandardModal
      isOpen={modal.isOpen}
      onClose={closeEditor}
      title={isEditing ? 'Edit Tax Rate' : 'Add Tax Rate'}
      size="md"
      bodyClassName="flex-1 overflow-y-auto p-6 space-y-4"
      footer={
        <>
          <button
            type="button"
            onClick={closeEditor}
            className="flex items-center justify-center px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button type="button" onClick={handleSave} className="btn-primary text-sm">
            {isEditing ? 'Update Tax Rate' : 'Add Tax Rate'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="taxName" className="block mb-1 text-sm font-medium text-gray-700">
            Tax Rate Name <span className="text-red-500">*</span>
          </label>
          <input
            id="taxName"
            type="text"
            value={form.name}
            onChange={e => dispatch(updateTaxRateForm({ field: 'name', value: e.target.value }))}
            placeholder="e.g. GST Included"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <div>
          <label htmlFor="taxRate" className="block mb-1 text-sm font-medium text-gray-700">
            Tax Rate (%) <span className="text-red-500">*</span>
          </label>
          <input
            id="taxRate"
            type="number"
            step="0.01"
            min="0"
            value={form.rate}
            onChange={e => dispatch(updateTaxRateForm({ field: 'rate', value: e.target.value }))}
            placeholder="0.00"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <div>
          <label htmlFor="taxInclusion" className="block mb-1 text-sm font-medium text-gray-700">
            Tax Inclusion <span className="text-red-500">*</span>
          </label>
          <p className="mb-2 text-xs text-gray-500">
            Set whether prices are inclusive or exclusive of tax.
          </p>
          <select
            id="taxInclusion"
            value={form.inclusion}
            onChange={e =>
              dispatch(updateTaxRateForm({ field: 'inclusion', value: e.target.value }))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="inclusive">Inclusive of tax</option>
            <option value="exclusive">Exclusive of tax</option>
          </select>
        </div>
      </div>
    </StandardModal>
  );
};

export default TaxRateEditorModal;
